import { PermissionsMap, sanitizePermissions, ALL_PERMISSIONS_GRANTED } from '@/constants/permissions';

/**
 * Monta o `permissions` do usuário transformado (login/verify2FA/verifyToken/refreshUserData em
 * AuthContext.tsx — as 4 cópias praticamente idênticas desse bloco). Extraído pra ser testável e
 * pra nunca mais divergir entre os 4 fluxos de autenticação (mesma lição já registrada no
 * cabeçalho de constants/permissions.ts sobre a duplicação de PERMISSION_KEYS).
 *
 * Achado real (relato do usuário, 09/09): "não está exibindo a lista de prestadores... nem o
 * ícone de prestador" no board Kanban — `isAdminRole` já era calculado em AuthContext.tsx, mas
 * nunca era usado pra decidir as permissões (só pra decidir `role: ADMIN/USER`). Um
 * "Administrador" cuja role no banco não tem uma permissão nova (ex.: `providers`, adicionada ao
 * schema depois da role já existir — mesmo achado documentado em
 * PENDING_PRODUCTION_MIGRATIONS.md) ficava com essa permissão `false`, mesmo sendo admin de
 * verdade. `hasPermission()` (o helper exposto pelo contexto) já bypassava certo via
 * `user.role === ADMIN`; o bug só afetava quem lê `user.permissions.X` direto (ex.:
 * DemandsBoard.tsx, `hasProviders`), sem passar por esse helper.
 */
export function resolveUserPermissions(
  isAdminRole: boolean,
  rolePermissions: Partial<Record<string, unknown>> | null | undefined
): PermissionsMap {
  return isAdminRole ? ALL_PERMISSIONS_GRANTED : sanitizePermissions(rolePermissions);
}


/** Rotas onde não faz sentido tentar verificar sessão (nem chamar o backend) — evita 401 previsíveis. */
export function isPublicAuthPath(
  path: string,
  routes: { HOME: string; LOGIN: string; UNAUTHORIZED: string; PUBLIC_DEMAND: string },
): boolean {
  return (
    path === routes.HOME ||
    path === '/home' ||
    path === routes.LOGIN ||
    path === routes.UNAUTHORIZED ||
    path.startsWith(routes.PUBLIC_DEMAND)
  );
}

interface NetworkErrorLike {
  code?: string;
  message?: string;
}

/** Detecta erros de rede/conexão (backend indisponível, crash, etc.) a partir de um erro do axios. */
export function isNetworkError(error: NetworkErrorLike): boolean {
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ERR_SOCKET_NOT_CONNECTED' ||
    error.code === 'ERR_CONNECTION_RESET' ||
    error.code === 'ECONNABORTED' ||
    Boolean(error.message?.includes('Network Error')) ||
    Boolean(error.message?.includes('ECONNREFUSED')) ||
    Boolean(error.message?.includes('ERR_EMPTY_RESPONSE')) ||
    Boolean(error.message?.includes('ECONNRESET'))
  );
}
